// components/Hero/Hero.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Crown } from 'lucide-react';
import { API_BASE } from '../config/api';
import './css/hero.css';
import HeroImg from './images/landing.webp';

const Hero = () => {
  const defaultHero = useMemo(
    () => ({
      badge_text: 'استشارات بحثية • تميز أكاديمي',
      title_prefix: 'لخدمات البحث العلمي',
      title_highlight: 'والتوجيه الأكاديمي',
      subtitle_text:
        'رسالتي هي اختصار الطريق عليكِ، وحمايتك من الأخطاء المنهجية، ومساعدتك على فهم بحثك بعمق، لا مجرد إنهائه.\nاستشارات مبنية على خبرة واقعية داخل البيئة الأكاديمية.',
      primary_cta_text: 'احجزي جلستك الآن',
      explore_text: 'من أنا؟',
      trust_rating_number: '1:1',
      trust_rating_text: 'استشارات',
      stat1_number: '',
      stat1_label: 'توجيه أكاديمي',
      stat2_number: '',
      stat2_label: 'للطالبات فقط',
    }),
    []
  );

  const [hero, setHero] = useState(defaultHero);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API_BASE}/api/hero`)
      .then((res) => {
        const dataHero = res.data?.hero;
        if (!mounted) return;
        setHero(dataHero ? { ...defaultHero, ...dataHero } : defaultHero);
      })
      .catch((err) => {
        console.error('Failed to load hero from API:', err);
        if (!mounted) return;
        setHero(defaultHero);
      })
      .finally(() => {
        if (!mounted) return;
      });

    return () => {
      mounted = false;
    };
  }, [defaultHero]);

  const formatTrustItemText = (num, label) => {
    const n = num === undefined || num === null ? '' : String(num).trim();
    const l = label === undefined || label === null ? '' : String(label).trim();
    if (!l) return '';
    return n ? `${n} ${l}` : l;
  };

  const badgeCircleText = useMemo(() => {
    const t = hero.badge_text ? String(hero.badge_text).trim() : '';
    if (!t) return '';
    // Store badge text in DB exactly as you want it displayed,
    // including any bullets between items.
    return t;
  }, [hero.badge_text]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  return (
    <section className="hero-section" dir="rtl">
      <div className="container h-100">
        <div className="row h-100 align-items-center">
          
          {/* Right Column: Text Content */}
          <div className="col-lg-6 col-md-12 order-2 order-lg-1">
            <motion.div 
              className="hero-content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
           
              
              <motion.h1 variants={itemVariants} className="hero-title">
                {hero.title_prefix} <br />
                <span className="highlight">{hero.title_highlight}</span>
              </motion.h1>
              
              <motion.p variants={itemVariants} className="hero-description">
                {hero.subtitle_text}
              </motion.p>

              <motion.div variants={itemVariants} className="hero-actions">
                <Link to="/booking" className="btn-hero-primary">
                  {hero.primary_cta_text}
                </Link>
                <Link to="/about" className="btn-hero-outline">
                  {hero.explore_text}
                </Link>
              </motion.div>

              {/* Trust indicators */}
              <motion.div variants={itemVariants} className="hero-trust">
                <div className="trust-item">
                  <span className="check-icon">
                    <Check size={12} strokeWidth={3} />
                  </span>{' '}
                  {formatTrustItemText(hero.trust_rating_number, hero.trust_rating_text)}
                </div>
                <div className="trust-item">
                  <span className="check-icon">
                    <Check size={12} strokeWidth={3} />
                  </span>{' '}
                  {formatTrustItemText(hero.stat1_number, hero.stat1_label)}
                </div>
                <div className="trust-item">
                  <span className="check-icon">
                    <Check size={12} strokeWidth={3} />
                  </span>{' '}
                  {formatTrustItemText(hero.stat2_number, hero.stat2_label)}
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Left Column: Image Composition */}
          <div className="col-lg-6 col-md-12 order-1 order-lg-2 position-relative">
            <motion.div 
              className="hero-image-wrapper"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
            >
              {/* Main Image */}
              <div className="image-container">
                <img 
                  src={HeroImg} 
                  alt="الدكتورة نهى الحازمي" 
                  className="main-hero-img"
                />
              </div>

              {/* Decorative Card (The "Nime" card from screenshot equivalent) */}
              

              {/* Floating Badge */}
              <motion.div 
                className="floating-badge"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <svg viewBox="0 0 100 100" width="100" height="100">
                  <path id="curve" d="M 50 50 m -37 0 a 37 37 0 1 1 74 0 a 37 37 0 1 1 -74 0" fill="transparent" />
                  <text>
                    <textPath href="#curve" fill="#ceaa78" textAnchor="middle" startOffset="50%">
                      {badgeCircleText}
                    </textPath>
                  </text>
                </svg>
                <div className="badge-center"><Crown size={28} strokeWidth={1.5} /></div>
              </motion.div>

            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;